import * as React from 'react';
import { cn } from '../utils/cn';

export interface SpinnerProps {
  /** Spinner size */
  size?: 'large' | 'default' | 'small';
  /** Additional class names */
  className?: string;
  /** Inline style */
  style?: React.CSSProperties;
  /** Whether spinner is active (always true if not specified) */
  spinning?: boolean;
  /** Content to wrap with loading overlay */
  children?: React.ReactNode;
}

const sizeMap = {
  small: 'h-4 w-4 border-2',
  default: 'h-6 w-6 border-2',
  large: 'h-10 w-10 border-3',
};

export function Spinner({
  size = 'default',
  className,
  style,
  spinning = true,
  children,
}: SpinnerProps) {
  if (!spinning) {
    return <>{children}</>;
  }

  const spinner = (
    <div
      className={cn(
        'animate-spin rounded-full border-accent border-t-transparent',
        sizeMap[size] || sizeMap.default,
        className
      )}
      style={style}
      role="status"
      aria-label="Laddar"
    />
  );

  // If wrapping children, show as overlay
  if (children) {
    return (
      <div className="relative">
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
          {spinner}
        </div>
        <div className="opacity-40 pointer-events-none">
          {children}
        </div>
      </div>
    );
  }

  return spinner;
}

/**
 * Legacy antd Spin compat — same API surface
 * Usage: <Spin size="large" /> or <Spin>{children}</Spin>
 */
export const Spin = Spinner;
